import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/', testId: 'nav-home-link' },
    { name: 'About', path: '/about', testId: 'nav-about-link' },
    { name: 'Services', path: '/services', testId: 'nav-services-link' },
    { name: 'Portfolio', path: '/portfolio', testId: 'nav-portfolio-link' },
    { name: 'Testimonials', path: '/testimonials', testId: 'nav-testimonials-link' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-eclipse-bg/70 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_freelance-hub-570/artifacts/3r30mrpu_EclipseProfessionalsLogo.png"
              alt="EclipseProfessionals"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={link.testId}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-brand-cyan'
                    : 'text-eclipse-text hover:text-brand-cyan'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="bg-brand-cyan text-eclipse-bg font-bold px-8 py-3 rounded-full hover:bg-white transition-all duration-300 hover:scale-105 inline-block"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-eclipse-text"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-eclipse-surface border-t border-white/5" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`${link.testId}-mobile`}
                className={`block text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-brand-cyan'
                    : 'text-eclipse-text hover:text-brand-cyan'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-contact-cta"
              className="block bg-brand-cyan text-eclipse-bg font-bold px-8 py-3 rounded-full hover:bg-white transition-all duration-300 text-center"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;